const KNOWN_BRANDS = [
  'nike',
  'adidas',
  'puma',
  'skechers',
  'mizuno',
  'new balance',
  'under armour',
];

const KNOWN_MODELS = [
  'mercurial vapor',
  'mercurial superfly',
  'tiempo',
  'phantom',
  'premier',
  'predator',
  'f50',
  'copa',
  '11pro',
  'one',
  'future',
  'king',
  'ultra',
  'razor',
  'pulse',
  'glide',
  'spark',
  'rebula',
  'morelia',
  'wave',
  'neo',
  'furon',
  'tekela',
  'audazo',
  '442',
  'magnetico',
  'clone',
  'spotlight',
  'blur',
  'generic',
  'custom',
  'x',
];

const SOLE_TYPE_MAP = {
  fg: 'fg',
  'firm ground': 'fg',
  'firm': 'fg',
  sg: 'sg',
  'soft ground': 'sg',
  'soft': 'sg',
  ag: 'ag',
  'artificial ground': 'ag',
  'artificial': 'ag',
  tf: 'tf',
  turf: 'tf',
  ic: 'ic',
  indoor: 'ic',
  in: 'ic',
  mg: 'mg',
  'multi ground': 'mg',
  'multi': 'mg',
  hg: 'hg',
  'hard ground': 'hg',
  'hard': 'hg',
};

const CONDITION_MAP = {
  newtags: 'newtags',
  'new tags': 'newtags',
  'new with tags': 'newtags',
  withouttags: 'withouttags',
  'without tags': 'withouttags',
  'new without tags': 'withouttags',
  vgood: 'vgood',
  'very good': 'vgood',
  good: 'good',
  satisfactory: 'satisfactory',
};

const normalizeText = value =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');

const normalizeSizeValue = value => {
  const normalized = normalizeText(value).replace(/^size\s+/, '');
  const compact = normalized.replace(/\s+/g, '');
  const ukFirstMatch = normalized.match(/^uk\s*(\d{1,2})(?:\.(5))?$/);
  const ukLastMatch = normalized.match(/^(\d{1,2})(?:\.(5))?\s*uk$/);
  const bareMatch = normalized.match(/^(\d{1,2})(?:\.(5))?$/);

  if (/^uk\d{1,2}5?$/.test(compact)) {
    return compact;
  }
  if (ukFirstMatch) {
    return `uk${ukFirstMatch[1]}${ukFirstMatch[2] ? '5' : ''}`;
  }
  if (ukLastMatch) {
    return `uk${ukLastMatch[1]}${ukLastMatch[2] ? '5' : ''}`;
  }
  if (bareMatch) {
    return `uk${bareMatch[1]}${bareMatch[2] ? '5' : ''}`;
  }

  return normalized;
};

const unique = values => [...new Set(values.filter(Boolean))];

const parseJsonValue = value => {
  if (typeof value !== 'string') {
    return null;
  }

  try {
    return JSON.parse(value.trim());
  } catch (e) {
    return null;
  }
};

const scalarValue = value => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value.value ?? value.option ?? value.key ?? null;
  }

  return value;
};

const splitValue = value => {
  if (Array.isArray(value)) {
    return value.flatMap(splitValue);
  }

  if (value == null) {
    return [];
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return [];
    }

    const parsed = parseJsonValue(trimmed);
    if (Array.isArray(parsed)) {
      return parsed.flatMap(splitValue);
    }
    if (parsed && typeof parsed === 'object') {
      return [];
    }

    return trimmed
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);
  }

  return [String(value)];
};

const getTags = product => splitValue(product?.tags);

const getOptionValues = (product, optionNames) => {
  const normalizedNames = optionNames.map(normalizeText);
  const options = product?.options || [];

  return options.flatMap(option => {
    const optionName = normalizeText(option?.name);
    return normalizedNames.includes(optionName) ? splitValue(option?.values) : [];
  });
};

const getMetafieldValues = (product, keys) => {
  const normalizedKeys = keys.map(normalizeText);
  const edges = product?.metafields?.edges || [];
  const metafields = Array.isArray(product?.metafields)
    ? product.metafields
    : edges.map(edge => edge?.node);

  return metafields.flatMap(metafield => {
    const key = normalizeText(metafield?.key);
    return normalizedKeys.includes(key) ? splitValue(metafield?.value) : [];
  });
};

const getFirstMetafieldValue = (product, keys) => getMetafieldValues(product, keys)[0];

const getMetafields = entity => {
  const edges = entity?.metafields?.edges || [];
  return Array.isArray(entity?.metafields) ? entity.metafields : edges.map(edge => edge?.node);
};

const getMetafieldValueByKeys = (entity, keys) => {
  const normalizedKeys = keys.map(normalizeText);
  const metafield = getMetafields(entity).find(item =>
    normalizedKeys.includes(normalizeText(item?.key))
  );

  return metafield?.value;
};

const getParsedMetafieldObject = (entity, keys) => {
  const value = getMetafieldValueByKeys(entity, keys);
  const parsed = parseJsonValue(value);
  return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
};

const getTaggedValues = (tags, keys) => {
  const normalizedKeys = keys.map(normalizeText);

  return tags.flatMap(tag => {
    const raw = String(tag || '').trim();
    const normalized = normalizeText(raw);
    const separatorMatch = raw.match(/^([^:=]+)\s*[:=]\s*(.+)$/);

    if (separatorMatch) {
      const key = normalizeText(separatorMatch[1]);
      return normalizedKeys.includes(key) ? splitValue(separatorMatch[2]) : [];
    }

    const prefixedKey = normalizedKeys.find(key => normalized.startsWith(`${key} `));
    return prefixedKey ? [raw.slice(prefixedKey.length).trim()] : [];
  });
};

const mapAdKid = values =>
  unique(
    values.map(normalizeText).map(value => {
      if (['ad', 'adult', 'adults', 'men', 'mens', 'women', 'womens'].includes(value)) {
        return 'Ad';
      }
      if (['kid', 'kids', 'junior', 'juniors', 'youth', 'children', 'child'].includes(value)) {
        return 'Kid';
      }
      return null;
    })
  );

const mapSize = values =>
  unique(
    values
      .map(value => normalizeSizeValue(String(value)))
      .map(value => {
        // Already compact format: uk8, uk85, uk10, uk105
        const compact = value.replace(/\s+/g, '');
        if (/^uk\d{1,2}5?$/.test(compact)) {
          return compact;
        }

        // UK-prefixed sizes: uk 8, uk8.5, uk 10.5
        const ukMatch = value.match(/^uk\s*(\d{1,2})(?:\.5)?$/);
        if (ukMatch) {
          const whole = ukMatch[1];
          const hasHalf = /\.5$/.test(value);
          return `uk${whole}${hasHalf ? '5' : ''}`;
        }

        // UK-suffixed sizes: 8 uk, 8.5 uk, 10 uk
        const ukSuffixMatch = value.match(/^(\d{1,2})(?:\.5)?\s*uk$/);
        if (ukSuffixMatch) {
          const whole = ukSuffixMatch[1];
          const hasHalf = /\.5$/.test(value);
          return `uk${whole}${hasHalf ? '5' : ''}`;
        }

        // Bare sizes: 8, 10, 11, 8.5, 10.5
        const bareMatch = value.match(/^(\d{1,2})(?:\.5)?$/);
        if (bareMatch) {
          const whole = bareMatch[1];
          const hasHalf = /\.5$/.test(value);
          return `uk${whole}${hasHalf ? '5' : ''}`;
        }

        return null;
      })
      .filter(Boolean)
  );

const getVariantNodes = product => {
  const variants = product?.variants;
  if (Array.isArray(variants)) {
    return variants;
  }
  return variants?.edges?.map(edge => edge?.node).filter(Boolean) || [];
};

const getVariantId = variant =>
  String(variant?.id || '')
    .split('/')
    .pop() || null;

const getInventoryItemId = variant =>
  String(variant?.inventoryItem?.id || variant?.inventory_item_id || '')
    .split('/')
    .pop() || null;

const getVariantSelectedOptionValues = variant => {
  const selectedOptions = Array.isArray(variant?.selectedOptions) ? variant.selectedOptions : [];
  return selectedOptions
    .filter(option => normalizeText(option?.name).includes('size'))
    .map(option => option?.value);
};

const getRestVariantOptionValues = variant =>
  [variant?.option1, variant?.option2, variant?.option3].filter(Boolean);

const getVariantSize = variant => {
  const values = [
    ...getVariantSelectedOptionValues(variant),
    ...getRestVariantOptionValues(variant),
    variant?.title,
  ];
  return mapSize(values)[0] || null;
};

const formatShoeSize = size => {
  if (typeof size !== "string") return size;

  const match = size.trim().match(/^uk(\d+)$/i);
  if (!match) return size;

  let value = match[1];

  if (value.length > 1 && value.endsWith("5")) {
    value = `${value.slice(0, -1)}.5`;
  }

  return `UK ${value}`;
};

const getVariantPriceAmount = variant => {
  const price = variant?.price;
  if (price && typeof price === 'object') {
    return Number(price.amount);
  }
  return Number(price);
};

const getVariantInventory = variant =>
  Number(variant?.inventoryQuantity ?? variant?.inventory_quantity ?? 0);

const getSharetribeProductVariantsFromShopify = ({ product, currency = 'GBP', rate = 1 } = {}) => {
  const variants = getVariantNodes(product);

  console.log("================VARIANTS==============");
  console.dir(variants, { depth: null, colors: true });
  console.log("================VARIANTS==============");

  return variants
    .map(variant => {
      const size = getVariantSize(variant);
      const sizeLabel = formatShoeSize(size);
      const amount = getVariantPriceAmount(variant);
      const stock = getVariantInventory(variant);

      if (!size || !Number.isFinite(amount)) {
        return null;
      }

      return compactPublicData({
        id: getVariantId(variant),
        shopifyVariantId: getVariantId(variant),
        shopifyInventoryItemId: getInventoryItemId(variant),
        title: sizeLabel,
        size,
        sizeLabel,
        sku: variant?.sku,
        stock: Number.isFinite(stock) ? Math.max(stock, 0) : 0,
        priceInSubunits: Math.round(amount * rate * 100),
        currency,
        available: variant?.availableForSale !== false && stock > 0,
      });
    })
    .filter(Boolean);
};

const mapSoleType = values =>
  unique(values.map(value => SOLE_TYPE_MAP[normalizeText(value)] || null));

const mapProductCondition = values => {
  const condition = values.map(value => CONDITION_MAP[normalizeText(value)]).find(Boolean);
  return condition || 'newtags';
};

const findKnownValue = (values, knownValues) => {
  const normalizedValues = values.map(normalizeText);
  return knownValues.find(known => normalizedValues.includes(known)) || null;
};

const findKnownValueInText = (text, knownValues) => {
  const normalizedText = ` ${normalizeText(text)} `;
  return knownValues.find(known => normalizedText.includes(` ${known} `)) || null;
};

const compactPublicData = data =>
  Object.entries(data).reduce((acc, [key, value]) => {
    if (Array.isArray(value) && value.length === 0) {
      return acc;
    }
    if (value === undefined || value === null || value === '') {
      return acc;
    }
    return { ...acc, [key]: value };
  }, {});

const SHOPIFY_WEIGHT_UNIT_MAP = {
  GRAMS: 'g',
  KILOGRAMS: 'kg',
  OUNCES: 'oz',
  POUNDS: 'lb',
  g: 'g',
  kg: 'kg',
  oz: 'oz',
  lb: 'lb',
};

const normalizeUnit = value => {
  const normalized = String(scalarValue(value) || '').trim();
  return (
    SHOPIFY_WEIGHT_UNIT_MAP[normalized] ||
    SHOPIFY_WEIGHT_UNIT_MAP[normalized.toUpperCase()] ||
    normalized
  );
};

const getVariantWeight = variant => {
  const measurementWeight = variant?.inventoryItem?.measurement?.weight;
  const weight = measurementWeight?.value ?? variant?.weight ?? variant?.grams;
  const unit =
    normalizeUnit(measurementWeight?.unit) ||
    normalizeUnit(variant?.weight_unit) ||
    (variant?.grams ? 'g' : null);

  return { weight, unit };
};

const getFirstGraphqlVariant = product => product?.variants?.edges?.[0]?.node;
const firstValue = (...values) =>
  values.find(value => value !== undefined && value !== null && value !== '');
const mergeVariantSources = ({ product, variant }) => {
  const graphqlVariant = getFirstGraphqlVariant(product);
  return {
    ...(graphqlVariant || {}),
    ...(variant || {}),
    metafields: variant?.metafields || graphqlVariant?.metafields,
    inventoryItem: variant?.inventoryItem || graphqlVariant?.inventoryItem,
  };
};

const getShippingDimensionsObject = ({ product, variant }) => {
  return (
    getParsedMetafieldObject(product, [
      'shippingDimensions',
      'shipping_dimensions',
      'shipping dimension',
      'shipping dimensions',
      'dimensions',
      'package_dimensions',
      'packageDimensions',
    ]) ||
    getParsedMetafieldObject(variant, [
      'shippingDimensions',
      'shipping_dimensions',
      'shipping dimension',
      'shipping dimensions',
      'dimensions',
      'package_dimensions',
      'packageDimensions',
    ]) ||
    {}
  );
};

const getDimensionValue = ({
  product,
  variant,
  objectValue,
  objectKeys,
  metafieldKeys,
  fallback,
}) => {
  const objectMatch = objectKeys
    .map(key => scalarValue(objectValue?.[key]))
    .find(value => value != null && value !== '');

  return firstValue(
    objectMatch,
    getFirstMetafieldValue(product, metafieldKeys),
    getFirstMetafieldValue(variant, metafieldKeys),
    fallback
  );
};

const getSharetribeShippingDimensionsFromShopify = ({
  product,
  variant,
  defaultDimensions = {},
}) => {
  const selectedVariant = mergeVariantSources({ product, variant });
  const variantWeight = getVariantWeight(selectedVariant);
  const shippingObject = getShippingDimensionsObject({ product, variant: selectedVariant });
  const metafieldWeight = firstValue(
    shippingObject.weight,
    getFirstMetafieldValue(product, ['weight', 'shipping_weight', 'package_weight']),
    getFirstMetafieldValue(selectedVariant, ['weight', 'shipping_weight', 'package_weight'])
  );
  const metafieldUnit = firstValue(
    shippingObject.unit,
    shippingObject.massUnit,
    shippingObject.weightUnit,
    getFirstMetafieldValue(product, [
      'unit',
      'mass_unit',
      'massUnit',
      'weight_unit',
      'shipping_weight_unit',
      'package_weight_unit',
    ]),
    getFirstMetafieldValue(selectedVariant, [
      'unit',
      'mass_unit',
      'massUnit',
      'weight_unit',
      'shipping_weight_unit',
      'package_weight_unit',
    ])
  );
  const weight = firstValue(
    scalarValue(metafieldWeight),
    variantWeight.weight,
    defaultDimensions.weight
  );
  const unit = normalizeUnit(metafieldUnit) || variantWeight.unit;
  const numericWeight = Number(weight);
  const height = getDimensionValue({
    product,
    variant: selectedVariant,
    objectValue: shippingObject,
    objectKeys: ['height'],
    metafieldKeys: ['height', 'shipping_height', 'package_height'],
    fallback: defaultDimensions.height,
  });
  const length = getDimensionValue({
    product,
    variant: selectedVariant,
    objectValue: shippingObject,
    objectKeys: ['length'],
    metafieldKeys: ['length', 'shipping_length', 'package_length'],
    fallback: defaultDimensions.length,
  });
  const width = getDimensionValue({
    product,
    variant: selectedVariant,
    objectValue: shippingObject,
    objectKeys: ['width'],
    metafieldKeys: ['width', 'shipping_width', 'package_width'],
    fallback: defaultDimensions.width,
  });
  const distanceUnit = normalizeUnit(
    getDimensionValue({
      product,
      variant: selectedVariant,
      objectValue: shippingObject,
      objectKeys: ['distanceUnit', 'distance_unit', 'dimensionUnit', 'dimension_unit'],
      metafieldKeys: [
        'distance_unit',
        'distanceUnit',
        'dimension_unit',
        'dimensionUnit',
        'shipping_distance_unit',
        'package_distance_unit',
      ],
      fallback: defaultDimensions.distanceUnit,
    })
  );

  if (!Number.isFinite(numericWeight) || numericWeight <= 0 || !unit) {
    return null;
  }

  return {
    ...defaultDimensions,
    height: String(scalarValue(height)),
    length: String(scalarValue(length)),
    width: String(scalarValue(width)),
    distanceUnit,
    weight: String(numericWeight),
    unit,
  };
};

const getSharetribeProductPublicDataFromShopify = product => {
  const tags = getTags(product);
  const productType = product?.productType || product?.product_type;
  const title = product?.title || '';
  const vendor = product?.vendor || '';
  const isUsed = [product?.title, product?.description]
    .filter(Boolean)
    .some(text => text.toLowerCase().includes("used"));

  const adkidValues = [
    ...getMetafieldValues(product, ['adkid', 'adult_kid', 'adult kid']),
    ...getOptionValues(product, ['adkid', 'adult/kid', 'adult kid', 'age group']),
    ...getTaggedValues(tags, ['adkid', 'adult_kid', 'adult kid', 'age group']),
    ...tags,
  ];

  const explicitBrandValues = [
    ...getMetafieldValues(product, ['brandname', 'brand']),
    ...getOptionValues(product, ['brand', 'brandname']),
    ...getTaggedValues(tags, ['brand', 'brandname']),
  ];

  const brandValues = [...explicitBrandValues, ...tags];

  const sizeValues = [
    ...getMetafieldValues(product, ['size']),
    ...getOptionValues(product, ['size', 'shoe size', 'uk size']),
    ...getTaggedValues(tags, ['size', 'shoe size', 'uk size']),
    ...tags,
  ];

  const conditionValues = [
    ...getMetafieldValues(product, ['productcondition', 'condition']),
    ...getOptionValues(product, ['condition', 'product condition']),
    ...getTaggedValues(tags, ['condition', 'productcondition']),
    ...tags,
  ];

  const soleValues = [
    ...getMetafieldValues(product, ['soletype', 'sole_type', 'sole type', 'sole']),
    ...getOptionValues(product, ['sole', 'sole type', 'soletype']),
    ...getTaggedValues(tags, ['sole', 'sole type', 'soletype']),
    ...tags,
  ];

  const explicitModelValues = [
    ...getMetafieldValues(product, ['model']),
    ...getOptionValues(product, ['model']),
    ...getTaggedValues(tags, ['model']),
  ];

  const modelValues = [...explicitModelValues, ...tags];

  const explicitBrand = explicitBrandValues.map(normalizeText).find(Boolean);
  const explicitModel = explicitModelValues.map(normalizeText).find(Boolean);
  const brand =
    findKnownValue(brandValues, KNOWN_BRANDS) ||
    explicitBrand ||
    findKnownValueInText(title, KNOWN_BRANDS);
  const model =
    findKnownValue(modelValues, KNOWN_MODELS) ||
    explicitModel ||
    findKnownValueInText(title, KNOWN_MODELS);

  const productcondition = mapProductCondition(conditionValues);

  return compactPublicData({
    adkid: mapAdKid(adkidValues),
    brandname: brand ? [brand] : undefined,
    size: mapSize(sizeValues),
    productcondition,
    listingType: isUsed ? "sell-used-products" : "sell-new-products",
    soletype: mapSoleType(soleValues),
    model: model ? [model] : undefined,
    ...(productType && { productType }),
  });
};

module.exports = {
  getTags,
  getSharetribeProductPublicDataFromShopify,
  getSharetribeShippingDimensionsFromShopify,
  getSharetribeProductVariantsFromShopify,
  normalizeSizeValue,
};
